// Colorways: N palettes without touching source image or alpha maps.
// Recoloring is one-directional: alpha maps fixed, only colors change.
// Blocker #11: alphaMapsUnchanged uses exact equality (bit-identical).

import type { Colorway, PaletteColor, LabColor, SeparationResult, AlphaMaps } from '../types';
import { labToRgbLinear } from '../color/spaces';

export function createColorway(
  id: string,
  name: string,
  colors: PaletteColor[]
): Colorway {
  return { id, name, colors: [...colors] };
}

export function recolorColorway(
  colorway: Colorway,
  colorIndex: number,
  newLab: LabColor
): Colorway {
  const nextColors = colorway.colors.map((c, i) => {
    if (i !== colorIndex) return c;
    return { ...c, lab: newLab };
  });
  return { ...colorway, colors: nextColors };
}

/**
 * Recompute preview from alpha maps + new colors (additive composite).
 * Alpha maps are NOT modified (blocker #4, #11).
 * Returns a linear-light PixelBuffer.
 */
export function applyColorway(
  result: SeparationResult,
  colorway: Colorway
): { width: number; height: number; data: Float32Array } {
  const { width, height } = result.alphaMaps;
  const nPixels = width * height;
  const nColors = colorway.colors.length;
  const data = new Float32Array(nPixels * 4);

  // Precompute linear RGB for each color in this colorway
  const linRgb = new Float32Array(nColors * 3);
  for (let k = 0; k < nColors; k++) {
    const lin = labToRgbLinear(colorway.colors[k].lab);
    linRgb[k * 3] = lin.r;
    linRgb[k * 3 + 1] = lin.g;
    linRgb[k * 3 + 2] = lin.b;
  }

  for (let i = 0; i < nPixels; i++) {
    let r = 0, g = 0, b = 0;
    for (let k = 0; k < nColors; k++) {
      const a = result.alphaMaps.maps[k].data[i];
      if (a <= 0) continue;
      r += linRgb[k * 3] * a;
      g += linRgb[k * 3 + 1] * a;
      b += linRgb[k * 3 + 2] * a;
    }
    const di = i * 4;
    data[di] = r;
    data[di + 1] = g;
    data[di + 2] = b;
    data[di + 3] = 1;
  }
  return { width, height, data };
}

export function alphaMapsUnchanged(
  a1: AlphaMaps,
  a2: AlphaMaps
): boolean {
  if (a1.maps.length !== a2.maps.length) return false;
  if (a1.width !== a2.width || a1.height !== a2.height) return false;
  for (let k = 0; k < a1.maps.length; k++) {
    const m1 = a1.maps[k].data;
    const m2 = a2.maps[k].data;
    if (m1.length !== m2.length) return false;
    // Blocker #11: exact equality (bit-identical), not 1e-6 tolerance
    for (let i = 0; i < m1.length; i++) {
      if (m1[i] !== m2[i]) return false;
    }
  }
  return true;
}
